import { Component, OnInit, ChangeDetectorRef, OnDestroy, ChangeDetectionStrategy } from "@angular/core";
import { CouponDetailViewModel } from "../../shared/models/coupon.model";
import { InternationalisationService } from "../../forked/angular-internationalisation";
import { CoreLogService } from "../../services/log.service";
import { Location } from "@angular/common";
import { ActivatedRoute, Router } from "@angular/router";
import { SnackBarService, snackBarMessageKey } from "../../services/snackBar.service";
import { RatingService } from "../../services/rating.service";
import { Subscription } from "rxjs";
import { ContentType, RedeemType, RegetrationTab, SnackBarMessageType } from "../../shared/contentTypes";
import { CouponDetailService } from "./couponDetail.service";
import { OverlaySpinnerService } from "../overlayspinner/overlaySpiner.service";
import { BottomSheetHelperService } from "../../services/bottomSheetHelper.service";
import { RatingDialogComponent } from "../../modalDialogs/ratingdialog/ratingDialog.component";
import { RouterHelperService } from "../../services/routerHelper.service";
import { CouponQRCodeDialogComponent } from "../../modalDialogs/couponqrcodedialog/couponqrcodedialog.component";
import { UserHelperService } from "../../services/userHelper.service";
import { CoreLanguageService } from "../../services/language.service";
import { environment } from "../../../../environments/environment";
import { WebShopService } from "../../services/webShop.service";
import { CouponRedemptionService } from "../../services/couponRedemption.service";
import { HYUAR_REDEMPTION_CODE_PREFIX, RedemptionResult } from "../../shared/enums/coupon.enums";
import { WebShopMode } from "../../services/IWebShopService";
import { LocationAndOrdertypeDialogComponent, WarningdialogComponent } from "../../modalDialogs";

@Component({
  selector: "coupondetail",
  templateUrl: "./couponDetail.component.html",
  styleUrls: ["./couponDetail.component.scss"],
  providers: [CouponDetailService, OverlaySpinnerService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CouponDetailComponent implements OnInit, OnDestroy {

  public dialogTitle: string = "";
  public dialogContent: string = "";
  public hasData: boolean = true; // check if there is data for this page;
  public panelOpenState: boolean = false;
  private couponId: number | string;
  public couponViewModel: CouponDetailViewModel;
  public fullDaysPanelOpened: boolean;
  public contentType = ContentType;
  public isLogin: boolean;
  public useCouponCode: boolean = false;
  public couponFilterLocationIds: number[] = [];

  private _langSubscriber: Subscription;
  private _ratingChangesSubscriber: Subscription;
  private _couponChangesSubscriber: Subscription;
  /** Guards redeemCoupon against a second concurrent tap firing a duplicate reservation. */
  private _isRedeeming: boolean = false;

  constructor(private _couponService: CouponDetailService
    , private _internationalisationService: InternationalisationService
    , private _changeDetectorRef: ChangeDetectorRef
    , private _coreLogService: CoreLogService
    , private _coreLanguageService: CoreLanguageService
    , private _userHelperService: UserHelperService
    , private _activatedRoute: ActivatedRoute
    , private _ratingService: RatingService
    , private _snackBarService: SnackBarService
    , private _router: Router
    , private _location: Location
    , private _routerHelperService: RouterHelperService
    , private _overlaySpinnerService: OverlaySpinnerService
    , private _bottomSheetHelperService: BottomSheetHelperService
    , private _webShopService: WebShopService
    , private _couponRedemptionService: CouponRedemptionService) {
    _changeDetectorRef.detach();
  }

  private PushChanges(): void {
    try {
      this._changeDetectorRef.detectChanges();
    } catch (e) {
      this._coreLogService.error("CouponDetailComponent.PushChanges", "_changeDetectorRef.detectChanges (catch)", e);
    }
  }

  /** True when the redeem button should appear dimmed — delegates to the single-source-of-truth service predicate. */
  public get isRedeemButtonDimmed(): boolean {
    const me = this;
    if (!me.couponViewModel) { return false; }
    return me._couponRedemptionService.isCouponDisabled(me.couponViewModel.id, me.couponViewModel.isRedeemed);
  }

  ngOnInit() {
    const me = this;
    me._activatedRoute.paramMap.subscribe(params => {
      me.couponId = params.get("id");
    });
    me.isLogin = me._userHelperService.IsRegistered;

    me.dialogTitle = me._coreLanguageService.value("phLoginDialog_Title").replace("<customer>", environment.CustomerName);
    me.dialogContent = me._coreLanguageService.value("phCouponDetail_Content").replace("<customer>", environment.CustomerName);

    me._langSubscriber = me._internationalisationService.localeChanged.subscribe(() => {
      me.PushChanges();
    });

    // reload coupon data if data has been changed in storage.
    me._couponChangesSubscriber = me._couponService.ObserveChanges.subscribe((changed) => {
      me.ReloadDataAndRender();
    });

    me.setRatingSubscriber();
    me.ReloadDataAndRender();
    me.isUseCouponCodeActive();
  }

  // check, if one rating of an item has been changed, then reload the view, with new data
  private setRatingSubscriber() {
    const me = this;
    me._ratingChangesSubscriber = me._ratingService.ObserveChanges.subscribe((changedRatings) => {
      let couponRatingChanged = false;
      const couponKeyPrefix = ContentType.Coupon + "|";
      for (const changedRating of changedRatings) {
        if (changedRating.toString().startsWith(couponKeyPrefix)) {
          couponRatingChanged = true;
          break;
        }
      }
      if (couponRatingChanged) {
        me.ReloadDataAndRender();
      }
    });
  }

  /**
  * Load coupons data
  */
  private ReloadDataAndRender(): void {
    const me = this;
    me._overlaySpinnerService.ShowLayoutSpinner();
    me._couponService.GetCouponViewModel(me.couponId)
      .then(coupon => {
        if (coupon) {
          me.hasData = true;
          me.couponViewModel = coupon;
          me.couponFilterLocationIds = coupon.locations;
          me.PushChanges();
        } else {
          me.hasData = false;
          // to avoid error for first binding
          me.couponViewModel = new CouponDetailViewModel();
          me.PushChanges();
        }
        me._overlaySpinnerService.HideLayoutSpinner();
      })
      .catch(e => {
        me._coreLogService.error("CouponDetailComponent.ReloadDataAndRender", "GetCouponViewModel", e);
        me._overlaySpinnerService.HideLayoutSpinner();
        me._snackBarService.ShowErrorMessageFailed(26);
      });
  }

  /**
  * Open a ModalDialog for rating an item or watching item rating info
  * @param news is empty if there is no rating yet. sending item Id and contentType to show rating data in modalDialog if user rate this item before.
  */
  public openRateModel(): void {
    const me = this;
    const ratingModel = me._bottomSheetHelperService.open(RatingDialogComponent, {
      data: { contentId: me.couponViewModel.id, contentType: ContentType.Coupon, title: me.couponViewModel.label },
    });
    // show snackBar after rating Item
    ratingModel.afterDismissed().subscribe((result: any) => {
      if (result) {
        result
          .then(() => {
            me._snackBarService.ShowMessage(snackBarMessageKey.successfullyExecuted, SnackBarMessageType.Successful);
          })
          .catch(e => {
            me._coreLogService.error("CouponDetailComponent.openRateModel", "ratingModel.afterClosed", e);
            me._snackBarService.ShowErrorMessageFailed(27);
          });
      }
    });
  }

  /** Opens the QR-code dialog; single-use coupons show a freshly reserved one-time code. */
  public async openQRCodeModel(coupon: CouponDetailViewModel): Promise<void> {
    const me = this;
    const loginRequired = coupon.isRedeemOncePerUser ? !me.isLogin : (!coupon.anonym_code && !me.isLogin);
    if (loginRequired) {
      me.openLoginDialog();
      return;
    }
    let qrCodeText = coupon.qrCodeText;
    let displayCode = coupon.qrCodeText;
    if (coupon.isRedeemOncePerUser) {
      const reservedCode = await me.reserveOneTimeCode(coupon.id);
      if (reservedCode === undefined) {
        return;
      }
      qrCodeText = HYUAR_REDEMPTION_CODE_PREFIX + "=" + reservedCode;
      displayCode = reservedCode;
    }
    me._bottomSheetHelperService.open(CouponQRCodeDialogComponent, {
      data: { id: coupon.id, label: coupon.label, text: coupon.text, qrCodeUrl: coupon.qrCodeUrl, qrCodeText, displayCode }
    });
  }

  /** Reserves fresh on every click, the server owns the expiry; undefined when the reserve failed. */
  private async reserveOneTimeCode(couponId: number | string): Promise<string | undefined> {
    const me = this;
    const reservation = await me._couponRedemptionService.reserveRedemption(couponId);
    if (reservation.result !== RedemptionResult.Proceed) {
      me._couponRedemptionService.routeRedemptionResult(reservation.result, () => me.PushChanges());
      return undefined;
    }
    return reservation.code;
  }

  private async isUseCouponCodeActive() {
    const me = this;
    const use_redeem_coupon = await me._webShopService.isCouponReedemAllowed(false);
    if (me.useCouponCode != use_redeem_coupon) {
      me.useCouponCode = use_redeem_coupon;
    }
  }

  /** Branches on isRedeemOncePerUser first; the single-redeemable path applies the nine-digit gate before delegating. */
  public async redeemCoupon(): Promise<void> {
    const me = this;
    if (me._isRedeeming) {
      return;
    }
    me._isRedeeming = true;
    try {
      const serviceMode = me._webShopService.getServiceMode();
      const locationId = me._webShopService.locationID;
      if (me.couponViewModel.isRedeemOncePerUser) {
        if (!locationId || !serviceMode) {
          me.openSelectLocationAndOrderType(me.couponViewModel.qrCodeText, true);
          return;
        }
        if (!me._couponRedemptionService.supportsSingleUseCoupon()) {
          me._couponRedemptionService.openUnsupportedOrderModeDialog();
          return;
        }
      } else {
        if (!locationId) {
          me.openSelectLocationAndOrderType(me.couponViewModel.qrCodeText, false);
          return;
        }
        const validOrderMode = me.isOrderModeValid(serviceMode);
        if (!validOrderMode) {
          me._couponRedemptionService.openUnsupportedOrderModeDialog();
          return;
        }
      }
      await me.resolveAndHandle(me.couponViewModel.qrCodeText);
    } finally {
      me._isRedeeming = false;
    }
  }

  /** Resolves a coupon code through the service funnel and routes the result. */
  private async resolveAndHandle(couponCode: string): Promise<void> {
    const me = this;
    const result = await me._couponRedemptionService.resolveAppCouponRedemption({
      id: me.couponViewModel.id,
      isRedeemed: me.couponViewModel.isRedeemed,
      couponCode: couponCode,
      anonymCode: me.couponViewModel.anonym_code,
      isRedeemOncePerUser: me.couponViewModel.isRedeemOncePerUser,
    });
    me.handleRedemptionResult(result);
  }

  /** Falsy when no order mode is selected; false for Webvoucher and Viewing. */
  private isOrderModeValid(orderMode: number): boolean | undefined {
    if (orderMode) {
      return (orderMode === WebShopMode.Webvoucher || orderMode === WebShopMode.Viewing) ? false : true;
    }
    return undefined;
  }

  /** Routes component-specific RedemptionResult cases; delegates common cases to the service router. */
  private handleRedemptionResult(result: RedemptionResult): void {
    const me = this;
    switch (result) {
      case RedemptionResult.LoginRequired:
        me.openLoginDialog();
        break;
      case RedemptionResult.UnsupportedOrderMode:
        me._couponRedemptionService.openUnsupportedOrderModeDialog();
        break;
      default:
        me._couponRedemptionService.routeRedemptionResult(result, () => me.PushChanges());
        break;
    }
  }

  /** Opens the location + order-type selection dialog; passes the nine-digit whitelist flag for single-redeemable coupons. */
  private openSelectLocationAndOrderType(qrCodeText: string, onlyNineDigitOrderTypes: boolean = false) {
    const me = this;
    const locationAndOrderTypeSelection = me._bottomSheetHelperService.open(LocationAndOrdertypeDialogComponent, {
      data: {
        locationIds: me.couponFilterLocationIds,
        redeemType: RedeemType.Coupon,
        onlyNineDigitOrderTypes,
      }
    })
    locationAndOrderTypeSelection.afterDismissed().subscribe(async (result) => {
      try {
        if (result && result.selectedLocation && result.selectedServiceMode) {
          if (result.selectedServiceMode !== WebShopMode.OrderToTable) {
            await me.initializeSelection(result.selectedServiceMode, result.selectedLocation);
            await me.resolveAndHandle(qrCodeText);
          } else {
            me.openWarningTableLoginRequired();
          }
        }
      } catch (e) {
        me._coreLogService.error("CouponDetailComponent.openSelectLocationAndOrderType", "afterDismissed callback", e);
      }
    });
  }

  private async initializeSelection(serviceMode: WebShopMode, selectedLocationID: number) {
    const me = this;
    try {
      await me._webShopService.setServiceMode(serviceMode);
      await me._webShopService.setServiceLocationByID(+selectedLocationID);
    } catch (error) {
      me._coreLogService.error("CouponDetailComponent.initializeSelection", "try catch", error);
    }
    me.PushChanges();
  }

  public openWarningTableLoginRequired() {
    const me = this;
    const warningButtomSheet = me._bottomSheetHelperService.open(WarningdialogComponent, {
      data: {
        title: "phCoupon_Redeem_Table_Login_Required_Dialog_Title",
        content: "phCoupon_Redeem_Table_Login_Required_Dialog_Desc",
        btnLabel: "phGlobal_Continue",
      },
      panelClass: "mat-bottom-sheet-container-medium",
    }
    );
    warningButtomSheet.afterDismissed().subscribe(async (result) => {
      if (result) {
        await me._webShopService.setServiceMode(WebShopMode.OrderToTable);
        me._router.navigate(["/qrcodescanner"]);
      }
    });
  }

  public navigateToRegister(): void {
    const me = this;
    me.navigateToRegistration(RegetrationTab.Register);
  }

  public navigateToLogin(): void {
    const me = this;
    me.navigateToRegistration(RegetrationTab.Login);
  }

  private navigateToRegistration(tabIndex: number): void {
    const me = this;
    me._routerHelperService.setRouteTargetDataByKey("returnUrl", me._router.routerState.snapshot.url);
    me._routerHelperService.setRouteTargetDataByKey("tabIndex", tabIndex);
    me._router.navigateByUrl("/registration");
  }

  /** On dismiss, refreshes isLogin + reloads the view model — detached CD means nothing else re-renders this component. */
  public openLoginDialog(): void {
    const me = this;
    me._couponRedemptionService.openLoginDialog(() => me.refreshAfterLogin());
  }

  /** Re-syncs login state + coupon view model after a login dialog closes. */
  private refreshAfterLogin(): void {
    const me = this;
    me.isLogin = me._userHelperService.IsRegistered;
    me.ReloadDataAndRender();
  }

  public navigateBack(): void {
    const me = this;
    me._location.back();
  }

  public swipeLeftHandler() {
    this._routerHelperService.navigateBackSimple();
  }

  ngOnDestroy(): void {
    const me = this;
    if (me._langSubscriber) {
      me._langSubscriber.unsubscribe();
    }
    if (me._ratingChangesSubscriber) {
      me._ratingChangesSubscriber.unsubscribe();
    }
    if (me._couponChangesSubscriber) {
      me._couponChangesSubscriber.unsubscribe();
    }
  }
}
